/**
 * Il campione di qualita' (GI-237): i casi estratti dalla base congelata, da
 * ricontrollare uno per uno.
 *
 * **Qui resta solo il ricontrollo.** Congelamento, campione, sigillo e
 * anonimizzazione erano quattro sezioni di questa pagina: oggi sono tappe
 * della consegna, dove si vede la strada intera. Il ricontrollo dei casi
 * resta qui perche' e' un lavoro a se', non una tappa: ogni caso si apre nel
 * banco e si giudica guardando il fotogramma.
 *
 * **Offline non muta.** Una revisione secondaria e' un atto verso il
 * committente, e una scrittura in coda locale sarebbe un giudizio che il
 * servente non ha mai visto.
 */
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { ServiceError } from '../api'
import { QUALITY_REVIEW_APPEND, type Whoami } from '../auth'
import { Severity } from '../design/indicators'
import { useT } from '../i18n/LocaleProvider'
import { addSecondary, readProgress, readSample } from '../quality'
import type { QualityProgress, QualitySample } from '../quality'

export const QUALITA = 'modulo-qualita'

function may(who: Whoami | null | undefined, action: string): boolean {
  return who == null || who.may.includes(action)
}

function decisionsOf(sample: QualitySample): Record<string, 'confirm' | 'overturn'> {
  const out: Record<string, 'confirm' | 'overturn'> = {}
  for (const one of sample.reviews) out[one.detection_id] = one.decision
  return out
}

export function InspectionQuality({
  inspectionId,
  who = null,
}: {
  inspectionId: string
  who?: Whoami | null
}): JSX.Element {
  const t = useT()
  const [progress, setProgress] = useState<QualityProgress | null>(null)
  const [sample, setSample] = useState<QualitySample | null>(null)
  const [reviewed, setReviewed] = useState<Record<string, 'confirm' | 'overturn'>>({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [online, setOnline] = useState(window.navigator.onLine)

  useEffect(() => {
    const onState = (): void => setOnline(window.navigator.onLine)
    window.addEventListener('online', onState)
    window.addEventListener('offline', onState)
    return () => {
      window.removeEventListener('online', onState)
      window.removeEventListener('offline', onState)
    }
  }, [])

  const refresh = useCallback(async (): Promise<void> => {
    const found = await readProgress(inspectionId)
    setProgress(found)
    if (found.sample_id) {
      const loaded = await readSample(inspectionId, found.sample_id)
      setSample(loaded)
      setReviewed(decisionsOf(loaded))
    } else {
      setSample(null)
      setReviewed({})
    }
  }, [inspectionId])

  useEffect(() => {
    let cancelled = false
    void refresh()
      .then(() => {
        if (!cancelled) setLoadError(null)
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setLoadError(error instanceof ServiceError ? error.message : t('quality.loadFailed'))
        }
      })
    return () => {
      cancelled = true
    }
  }, [refresh])

  async function judge(detectionId: string, decision: 'confirm' | 'overturn'): Promise<void> {
    if (!online || sample === null) return
    setBusy(true)
    setError(null)
    try {
      await addSecondary(inspectionId, { sample_id: sample.id, detection_id: detectionId, decision })
      await refresh()
      // Il giudizio appena dato resta scritto anche se la rilettura arriva
      // prima che il servizio lo mostri: e' cio' che l'operatore ha fatto.
      setReviewed((current) => ({ ...current, [detectionId]: decision }))
    } catch (error: unknown) {
      setError(error instanceof ServiceError ? error.message : t('quality.actionFailed'))
    } finally {
      setBusy(false)
    }
  }

  const canReview = online && !busy && may(who, QUALITY_REVIEW_APPEND)
  const done = sample !== null && sample.order.every((one) => reviewed[one] !== undefined)

  return (
    <main className="qualita" id={QUALITA}>
      <h2>{t('title.quality')}</h2>
      <p className="tenue">
        {t('quality.sampleIntro')}{' '}
        <Link to={`/inspections/${inspectionId}/delivery`}>{t('quality.openDelivery')}</Link>
      </p>
      {loadError !== null && <p role="alert">{loadError}</p>}
      {!online && <p role="note">{t('quality.offline')}</p>}
      {progress === null && loadError === null ? <p role="status">{t('quality.reading')}</p> : null}

      <section>
        <h3>{t('quality.secondary')}</h3>
        {progress !== null && sample === null && <p>{t('quality.needSample')}</p>}
        {sample !== null && (
          <>
            <p className="tenue">
              {t('quality.sampleMeta', { n: sample.required_size, algo: sample.algorithm_version, seed: sample.seed })}
              {done && <> · {t('quality.allReviewed')}</>}
            </p>
            <ol>
              {sample.order.map((detectionId) => {
                const caso = sample.cases.find((one) => one.detection_id === detectionId)
                return (
                  <li key={detectionId}>
                    {/* Che cosa si sta ricontrollando, non il suo identificativo:
                        il collegamento apre il candidato nel banco, perche' un
                        giudizio si da' guardando il fotogramma. */}
                    {caso === undefined ? (
                      <code>{detectionId.slice(0, 8)}</code>
                    ) : (
                      <Link to={`/inspections/${inspectionId}/candidates/${detectionId}`}>
                        <strong>{caso.code}</strong> <Severity level={caso.level} />
                        {caso.external_element_id !== null &&
                          ` · ${t('quality.caseElement', { n: caso.external_element_id })}`}
                      </Link>
                    )}
                    {caso !== undefined && <code className="tenue"> {detectionId.slice(0, 8)}</code>}
                    {reviewed[detectionId] !== undefined && ` · ${reviewed[detectionId]}`}
                    <button type="button" disabled={!canReview} onClick={() => void judge(detectionId, 'confirm')}>
                      {t('quality.confirm')}
                    </button>
                    <button type="button" disabled={!canReview} onClick={() => void judge(detectionId, 'overturn')}>
                      {t('quality.overturn')}
                    </button>
                  </li>
                )
              })}
            </ol>
          </>
        )}
        {busy && <p role="status">{t('quality.reviewing')}</p>}
        {error !== null && <p role="alert">{error}</p>}
      </section>
    </main>
  )
}
